import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Loader2, Save, Upload, Trash2, Plus, X, ExternalLink, Copy,
  Settings, Palette, Tag, Clock, Power
} from 'lucide-react';

const FRONTEND_BASE = window.location.origin;
const FORM_PATH = '/complaint';

const ComplaintFormSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [config, setConfig] = useState(null);
  const [newTypeInput, setNewTypeInput] = useState('');

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const res = await api.getComplaintFormConfig();
      setConfig(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load form config');
    } finally {
      setLoading(false);
    }
  };

  const update = (patch) => setConfig((prev) => ({ ...prev, ...patch }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        is_enabled: config.is_enabled,
        title: config.title,
        subtitle: config.subtitle,
        primary_color: config.primary_color,
        problem_types: config.problem_types,
        default_sla_hours: parseInt(config.default_sla_hours, 10),
        cta_text: config.cta_text,
        thank_you_message: config.thank_you_message,
        footer_text: config.footer_text,
      };
      const res = await api.updateComplaintFormConfig(payload);
      setConfig(res.data);
      toast.success('Form settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.uploadComplaintFormLogo(fd);
      setConfig((prev) => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    try {
      const res = await api.updateComplaintFormConfig({ logo_url: '' });
      setConfig({ ...res.data, logo_url: null });
      toast.success('Logo removed');
    } catch (err) {
      toast.error('Failed to remove logo');
    }
  };

  const addProblemType = () => {
    const v = newTypeInput.trim();
    if (!v) return;
    if (config.problem_types.includes(v)) {
      toast.error('Already exists');
      return;
    }
    update({ problem_types: [...config.problem_types, v] });
    setNewTypeInput('');
  };

  const removeProblemType = (t) => {
    update({ problem_types: config.problem_types.filter((x) => x !== t) });
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${FRONTEND_BASE}${FORM_PATH}`);
    toast.success('Link copied');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!config) return null;

  return (
    <div data-testid="complaint-form-settings" className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-4xl font-bold font-headings uppercase tracking-tight text-slate-900 mb-2">
          Public Complaint Form
        </h1>
        <p className="text-slate-600 font-body max-w-2xl">
          Customize the public-facing complaint form that students use to raise tickets without logging in.
        </p>
      </div>

      {/* Link bar */}
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-cyan-600 text-white rounded-lg flex items-center justify-center">
            <ExternalLink size={20} />
          </div>
          <div>
            <div className="text-xs text-cyan-700 font-semibold uppercase tracking-wider">Public URL</div>
            <div className="font-mono text-sm text-slate-900">{FRONTEND_BASE}{FORM_PATH}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={copyLink}
            data-testid="copy-public-url"
            className="px-3 py-1.5 text-sm bg-white border border-cyan-300 text-cyan-700 rounded-lg hover:bg-cyan-50 flex items-center gap-1.5"
          >
            <Copy size={14} /> Copy
          </button>
          <a
            href={FORM_PATH}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 text-sm bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 flex items-center gap-1.5"
          >
            <ExternalLink size={14} /> Open
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings (left, 2 cols) */}
        <div className="lg:col-span-2 space-y-5">
          {/* Enable / Disable */}
          <section className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Power size={20} className={config.is_enabled ? 'text-green-600' : 'text-slate-400'} />
                <div>
                  <div className="font-semibold text-slate-900">Form Status</div>
                  <div className="text-sm text-slate-500">
                    {config.is_enabled ? 'Currently accepting submissions' : 'Disabled — visitors will see an error'}
                  </div>
                </div>
              </div>
              <button
                onClick={() => update({ is_enabled: !config.is_enabled })}
                data-testid="toggle-enabled"
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  config.is_enabled ? 'bg-green-500' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    config.is_enabled ? 'translate-x-6' : ''
                  }`}
                />
              </button>
            </div>
          </section>

          {/* Branding */}
          <section className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
              <Palette size={18} className="text-cyan-600" />
              <h2 className="font-semibold text-slate-900">Branding</h2>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Title (big text at top)</label>
              <input
                type="text"
                data-testid="input-title"
                value={config.title || ''}
                onChange={(e) => update({ title: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Subtitle / Welcome Message</label>
              <textarea
                value={config.subtitle || ''}
                onChange={(e) => update({ subtitle: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 resize-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Primary Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={config.primary_color || '#0891b2'}
                    onChange={(e) => update({ primary_color: e.target.value })}
                    data-testid="input-color"
                    className="h-10 w-16 rounded border border-slate-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.primary_color || ''}
                    onChange={(e) => update({ primary_color: e.target.value })}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 font-mono text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">CTA Button Text</label>
                <input
                  type="text"
                  value={config.cta_text || ''}
                  onChange={(e) => update({ cta_text: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Logo / Banner Image (optional)</label>
              {config.logo_url ? (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <img src={config.logo_url} alt="Logo" className="h-14 w-auto rounded object-cover" />
                  <div className="flex-1 text-xs text-slate-500 truncate">{config.logo_url}</div>
                  <button
                    onClick={removeLogo}
                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ) : (
                <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg hover:border-cyan-500 cursor-pointer transition">
                  {uploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} className="text-slate-500" />}
                  <span className="text-sm text-slate-600">Click to upload (max 5MB)</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => { handleLogoUpload(e.target.files[0]); e.target.value = ''; }}
                  />
                </label>
              )}
            </div>
          </section>

          {/* Problem Types */}
          <section className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
              <Tag size={18} className="text-cyan-600" />
              <h2 className="font-semibold text-slate-900">Problem Categories</h2>
            </div>
            <p className="text-xs text-slate-500">Students pick from this list when submitting a complaint.</p>

            <div className="flex flex-wrap gap-2">
              {config.problem_types.map((t) => (
                <div
                  key={t}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cyan-50 border border-cyan-200 text-cyan-800 rounded-full text-sm"
                >
                  {t}
                  <button onClick={() => removeProblemType(t)} className="text-cyan-600 hover:text-red-600">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={newTypeInput}
                onChange={(e) => setNewTypeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addProblemType(); } }}
                placeholder="Add a category…"
                data-testid="input-new-type"
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
              />
              <button
                onClick={addProblemType}
                className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-700 flex items-center gap-1.5 text-sm"
              >
                <Plus size={16} /> Add
              </button>
            </div>
          </section>

          {/* Messages & SLA */}
          <section className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
              <Clock size={18} className="text-cyan-600" />
              <h2 className="font-semibold text-slate-900">Messages & SLA</h2>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Default SLA (hours)</label>
              <input
                type="number"
                min={1}
                max={720}
                value={config.default_sla_hours || 48}
                onChange={(e) => update({ default_sla_hours: e.target.value })}
                className="w-32 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
              />
              <span className="text-xs text-slate-500 ml-2">Tickets target resolution within this window.</span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Thank You Message (shown after submission)</label>
              <textarea
                value={config.thank_you_message || ''}
                onChange={(e) => update({ thank_you_message: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 resize-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Footer Note (small text under button)</label>
              <input
                type="text"
                value={config.footer_text || ''}
                onChange={(e) => update({ footer_text: e.target.value })}
                placeholder="e.g., We'll get back to you within 48 hours."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
              />
            </div>
          </section>

          {/* Save bar */}
          <div className="sticky bottom-0 bg-white border border-slate-200 rounded-xl p-4 shadow-lg flex items-center justify-between">
            <span className="text-sm text-slate-500">
              {config.updated_by && config.updated_at ? `Last edited by ${config.updated_by}` : 'Unsaved changes'}
            </span>
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-settings"
              className="px-5 py-2.5 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 flex items-center gap-2 font-medium"
            >
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              Save Changes
            </button>
          </div>
        </div>

        {/* Live Preview (right column) */}
        <div className="lg:col-span-1">
          <div className="sticky top-6">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Settings size={12} /> Live Preview
            </div>
            <div className="bg-slate-900 rounded-2xl p-4 shadow-xl">
              <div className="bg-white rounded-xl overflow-hidden">
                <div
                  className="px-5 py-6 text-white text-center"
                  style={{
                    background: config.logo_url
                      ? `url(${config.logo_url}) center/cover`
                      : `linear-gradient(135deg, ${config.primary_color} 0%, ${config.primary_color}cc 100%)`,
                  }}
                >
                  <div className={config.logo_url ? 'bg-black/40 rounded p-3 -m-2' : ''}>
                    <h3 className="text-lg font-bold mb-1">{config.title || 'Need Help?'}</h3>
                    <p className="text-xs text-white/80 line-clamp-2">{config.subtitle}</p>
                  </div>
                </div>
                <div className="p-4 space-y-2.5 text-xs">
                  <div className="border border-slate-200 rounded px-2.5 py-1.5 text-slate-400">Full name</div>
                  <div className="border border-slate-200 rounded px-2.5 py-1.5 text-slate-400">Phone</div>
                  <div className="border border-slate-200 rounded px-2.5 py-1.5 text-slate-400">
                    {config.problem_types[0] || 'Category'}
                  </div>
                  <div className="border border-slate-200 rounded px-2.5 py-3 text-slate-400">Describe your issue…</div>
                  <button
                    type="button"
                    className="w-full py-2.5 rounded-md text-white text-sm font-semibold mt-1"
                    style={{ backgroundColor: config.primary_color }}
                  >
                    {config.cta_text || 'Submit Complaint'}
                  </button>
                  <div className="text-center text-[10px] text-slate-400 pt-1">{config.footer_text}</div>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3 text-center">
              Changes are reflected after saving.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplaintFormSettings;
